var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOkapiKy } from '@folio/stripes/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { fetchRecordIndex } from '../api/recordService';
import { RecordType } from '../types/api';
/** Forward window size requested from the backend per page. */
var PAGE_SIZE = 50;
/** Backward window size requested when paging upward from an anchor. */
var BACKWARD_PAGE_SIZE = 50;
/** URL query-param key persisting the selected register type (CZNA-230). */
var TYPE_PARAM = 'indexType';
/** URL query-param key persisting the selected heading language (CZNA-1168). */
var LANGUAGE_PARAM = 'indexLanguage';
/** URL query-param key persisting the selected record status (CZNA-1379). */
var STATUS_PARAM = 'indexStatus';
var STATUSES = ['active', 'merged', 'deleted', 'all'];
/** Active records unless the URL names another known status. */
var parseStatus = function (raw) {
    return STATUSES.includes(raw !== null && raw !== void 0 ? raw : '') ? raw : 'active';
};
/** A MARC language code is three lowercase letters; anything else is no filter. */
var parseLanguage = function (raw) {
    return raw && /^[a-z]{3}$/.test(raw) ? raw : undefined;
};
/** Parse a RecordType from a URL value; undefined (all types) for empty/unknown. */
var parseRecordType = function (raw) {
    if (!raw)
        return undefined;
    return Object.values(RecordType).includes(raw)
        ? raw
        : undefined;
};
/**
 * CZNA-216 / CZNA-230 — hook backing the alphabetical heading register ("rejstřík").
 *
 * CZNA-230 generalizes the MVP (SUBJECT-only, forward-only) to:
 *  - any authority type via `recordType`, plus an "all types" untyped mode
 *    (`recordType === undefined`), persisted in the URL (`?indexType=…`);
 *  - bidirectional keyset paging (`loadMore` forward, `loadPrevious` backward) by
 *    wiring `before` + `anchorId`;
 *  - precise jumps to a specific heading (value + id) via `jumpTo(value, headingId)`,
 *    used by the VARIANT "viz {preferred}" cross-reference link.
 *
 * Pagination model: the backend positions a window at the first heading `>= anchor`
 * (Czech collation), tie-broken by `anchorId`. We accumulate fetched pages
 * client-side; `loadMore()` advances the cursor to the last accumulated entry,
 * `loadPrevious()` requests a backward window before the first accumulated entry.
 * The accumulator is de-duplicated by `headingId`.
 *
 * Unstable-`ky` trap (project MEMORY): `useOkapiKy()` returns a fresh `ky` each
 * render. We capture it in a ref and reference the ref inside the queryFn, and
 * key the query only on serializable params — never on `ky` — so it can't loop.
 */
var useRecordIndex = function () {
    var _a, _b;
    var ky = useOkapiKy();
    var kyRef = useRef(ky);
    kyRef.current = ky;
    var history = useHistory();
    var location = useLocation();
    // Selected register type, sourced from the URL. undefined = all types (untyped).
    var _c = useState(function () { return parseRecordType(new URLSearchParams(location.search).get(TYPE_PARAM)); }), recordType = _c[0], setRecordTypeState = _c[1];
    // CZNA-1168: selected heading language, sourced from the URL. undefined = all languages.
    var _d = useState(function () { return parseLanguage(new URLSearchParams(location.search).get(LANGUAGE_PARAM)); }), language = _d[0], setLanguageState = _d[1];
    // CZNA-1379: whose headings are shown, sourced from the URL. Active records by default —
    // deleted and merged ones are in the register only when asked for.
    var _e = useState(function () { return parseStatus(new URLSearchParams(location.search).get(STATUS_PARAM)); }), status = _e[0], setStatusState = _e[1];
    // `anchor` / `anchorId` form the keyset cursor sent to the backend. `direction`
    // selects whether we fetch a forward window (after) or a backward window (before).
    var _f = useState(''), anchor = _f[0], setAnchor = _f[1];
    var _g = useState(undefined), anchorId = _g[0], setAnchorId = _g[1];
    var _h = useState('forward'), direction = _h[0], setDirection = _h[1];
    // Accumulated entries across loaded pages (deduped by headingId).
    var _j = useState([]), entries = _j[0], setEntries = _j[1];
    var _k = useState(true), filterPaneIsVisible = _k[0], setFilterPaneIsVisible = _k[1];
    // Pending jump-to input (mirrors useRecordList's pending `query`).
    var _l = useState(''), jumpInput = _l[0], setJumpInput = _l[1];
    var _m = useQuery(['czna-authorities-record-index', recordType !== null && recordType !== void 0 ? recordType : 'ALL', language !== null && language !== void 0 ? language : 'ALL', status, anchor, anchorId !== null && anchorId !== void 0 ? anchorId : 0, direction], function () { return fetchRecordIndex(kyRef.current, {
        recordType: recordType,
        language: language,
        status: status,
        anchor: anchor || undefined,
        anchorId: anchor ? anchorId : undefined,
        after: direction === 'forward' ? PAGE_SIZE : 0,
        before: direction === 'backward' ? BACKWARD_PAGE_SIZE : 0,
    }); }, { keepPreviousData: true }), data = _m.data, isLoading = _m.isLoading, isFetching = _m.isFetching, isPreviousData = _m.isPreviousData, error = _m.error, refetch = _m.refetch;
    // CZNA-1168: the URL is the source of truth for the filters — a change that did not come
    // from the setters below (browser back/forward, a link to ?indexLanguage=…) re-syncs the
    // state and starts a fresh window. The setters' own `history.replace` lands here with the
    // value already in state, so it is a no-op for them.
    var filtersRef = useRef({ recordType: recordType, language: language, status: status });
    filtersRef.current = { recordType: recordType, language: language, status: status };
    useEffect(function () {
        var params = new URLSearchParams(location.search);
        var urlType = parseRecordType(params.get(TYPE_PARAM));
        var urlLanguage = parseLanguage(params.get(LANGUAGE_PARAM));
        var urlStatus = parseStatus(params.get(STATUS_PARAM));
        var current = filtersRef.current;
        if (urlType === current.recordType && urlLanguage === current.language
            && urlStatus === current.status)
            return;
        setRecordTypeState(urlType);
        setLanguageState(urlLanguage);
        setStatusState(urlStatus);
        setEntries([]);
        setDirection('forward');
        setAnchorId(undefined);
        setAnchor('');
    }, [location.search]);
    // Merge each fetched page into the accumulator, deduping by headingId. Forward
    // windows append; backward windows prepend (their rows sort before the current
    // first entry). On a reset (jumpTo / clear / type change) the explicit setters
    // clear the accumulator first, so a fresh window simply merges onto an empty list.
    useEffect(function () {
        // CZNA-1168: `keepPreviousData` keeps the OLD key's page as `data` until the new window
        // arrives. A reset that also flips `direction` (a jump or filter change after paging
        // upward) re-runs this effect, and without the guard it would merge that stale page back
        // into the freshly cleared accumulator — and the new window would then land next to it.
        if (!(data === null || data === void 0 ? void 0 : data.entries) || isPreviousData)
            return;
        setEntries(function (prev) {
            var seen = new Set(prev.map(function (e) { return e.headingId; }));
            var fresh = data.entries.filter(function (e) { return !seen.has(e.headingId); });
            if (fresh.length === 0)
                return prev;
            return direction === 'backward' ? __spreadArray(__spreadArray([], fresh, true), prev, true) : __spreadArray(__spreadArray([], prev, true), fresh, true);
        });
    }, [data, direction, isPreviousData]);
    // CZNA-230: jump to the first heading >= value (optionally tie-broken by headingId,
    // for a precise landing on a specific row — e.g. the VARIANT "viz" preferred target).
    var jumpTo = useCallback(function (term, headingId) {
        // Reset the accumulator first so the new window starts clean, then move the
        // cursor. Order matters: clearing entries before anchor change avoids a flash
        // of the old window merged with the new.
        setEntries([]);
        setDirection('forward');
        setAnchorId(headingId);
        setAnchor(term);
    }, []);
    // CZNA-230: switch the register type (or all-types when undefined). Resets the
    // accumulator + cursor and persists the selection in the URL so it survives refresh.
    var setRecordType = useCallback(function (next) {
        setRecordTypeState(next);
        setEntries([]);
        setDirection('forward');
        setAnchorId(undefined);
        setAnchor('');
        setJumpInput('');
        var params = new URLSearchParams(location.search);
        if (next) {
            params.set(TYPE_PARAM, next);
        }
        else {
            params.delete(TYPE_PARAM);
        }
        var search = params.toString();
        history.replace({ search: search ? "?".concat(search) : '' });
    }, [history, location.search]);
    // CZNA-1168: switch the heading language (or all languages when undefined). Same reset
    // and URL persistence as a type change; the jump input is kept, since "where am I in the
    // alphabet" still means something within one language.
    var setLanguage = useCallback(function (next) {
        setLanguageState(next);
        setEntries([]);
        setDirection('forward');
        setAnchorId(undefined);
        setAnchor('');
        var params = new URLSearchParams(location.search);
        if (next) {
            params.set(LANGUAGE_PARAM, next);
        }
        else {
            params.delete(LANGUAGE_PARAM);
        }
        var search = params.toString();
        history.replace({ search: search ? "?".concat(search) : '' });
    }, [history, location.search]);
    // CZNA-1379: switch whose headings are shown. Same reset and URL persistence as a language
    // change; the default (active) stays out of the URL.
    var setStatus = useCallback(function (next) {
        setStatusState(next);
        setEntries([]);
        setDirection('forward');
        setAnchorId(undefined);
        setAnchor('');
        var params = new URLSearchParams(location.search);
        if (next !== 'active') {
            params.set(STATUS_PARAM, next);
        }
        else {
            params.delete(STATUS_PARAM);
        }
        var search = params.toString();
        history.replace({ search: search ? "?".concat(search) : '' });
    }, [history, location.search]);
    var handleSearch = useCallback(function (e) {
        e.preventDefault();
        jumpTo(jumpInput);
    }, [jumpInput, jumpTo]);
    var handleClearSearch = useCallback(function () {
        setJumpInput('');
        jumpTo('');
    }, [jumpTo]);
    var loadMore = useCallback(function () {
        setEntries(function (prev) {
            if (prev.length === 0)
                return prev;
            var last = prev[prev.length - 1];
            // Advance the cursor forward without clearing the accumulator (append).
            setDirection('forward');
            setAnchorId(last.headingId);
            setAnchor(last.headingValue);
            return prev;
        });
    }, []);
    // CZNA-230: page upward — request a backward window before the first accumulated
    // entry. The fetched rows are prepended by the merge effect above.
    var loadPrevious = useCallback(function () {
        setEntries(function (prev) {
            if (prev.length === 0)
                return prev;
            var first = prev[0];
            setDirection('backward');
            setAnchorId(first.headingId);
            setAnchor(first.headingValue);
            return prev;
        });
    }, []);
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    var hasMoreAfter = (_a = data === null || data === void 0 ? void 0 : data.hasMoreAfter) !== null && _a !== void 0 ? _a : false;
    var hasMoreBefore = (_b = data === null || data === void 0 ? void 0 : data.hasMoreBefore) !== null && _b !== void 0 ? _b : false;
    return {
        recordType: recordType,
        setRecordType: setRecordType,
        language: language,
        setLanguage: setLanguage,
        status: status,
        setStatus: setStatus,
        entries: entries,
        anchor: anchor,
        jumpTo: jumpTo,
        loadMore: loadMore,
        loadPrevious: loadPrevious,
        hasMoreAfter: hasMoreAfter,
        hasMoreBefore: hasMoreBefore,
        isLoading: isLoading,
        isFetching: isFetching,
        error: error,
        // CZNA-538: the lists render a real error state now, and an error state without
        // a way out is a dead end — the retry button calls this.
        refetch: refetch,
        // SearchLayout ListState adapter fields
        query: jumpInput,
        setQuery: setJumpInput,
        handleSearch: handleSearch,
        handleClearSearch: handleClearSearch,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        totalRecords: entries.length,
    };
};
export default useRecordIndex;
